/**
 * The search box holds one string: free text plus `#tag` tokens. That string is
 * the single source of truth (and what goes in the URL as `q`); clicking a tag
 * anywhere in the UI toggles its token in the string rather than keeping a
 * second, separate tag selection that could disagree with what is typed.
 */

const TAG_TOKEN = /(^|\s)#([^\s#]+)/g

export interface ParsedQuery {
  /** Free text, with the tag tokens removed. */
  text: string
  /** Lower-cased, de-duplicated, in the order typed. */
  tags: string[]
}

export function parseQuery(input: string): ParsedQuery {
  const tags: string[] = []
  for (const m of input.matchAll(TAG_TOKEN)) {
    const tag = m[2]!.toLowerCase()
    if (!tags.includes(tag)) tags.push(tag)
  }
  const text = input.replace(TAG_TOKEN, ' ').replace(/\s+/g, ' ').trim()
  return { text, tags }
}

/** Add `#tag` to the input, or remove every occurrence of it if present. */
export function toggleTag(input: string, tag: string): string {
  const t = tag.toLowerCase()
  if (parseQuery(input).tags.includes(t)) {
    return input
      .replace(TAG_TOKEN, (whole, lead: string, found: string) =>
        found.toLowerCase() === t ? lead : whole
      )
      .replace(/\s+/g, ' ')
      .trim()
  }
  const base = input.trim()
  return base ? `${base} #${t}` : `#${t}`
}

/**
 * The `#partial` token the caret is in, if any — what tag autocomplete
 * completes. `start` is the index of the `#`.
 */
export function tagTokenAt(
  input: string,
  caret: number
): { start: number; partial: string } | null {
  const before = input.slice(0, caret)
  const m = /(^|\s)#([^\s#]*)$/.exec(before)
  if (!m) return null
  return { start: caret - m[2]!.length - 1, partial: m[2]!.toLowerCase() }
}

/** Replace the token at `start` (through the caret) with a completed `#tag `. */
export function completeTag(
  input: string,
  start: number,
  caret: number,
  tag: string
): { value: string; caret: number } {
  const insert = `#${tag} `
  const rest = input.slice(caret).replace(/^[^\s]*\s?/, '')
  return { value: input.slice(0, start) + insert + rest, caret: start + insert.length }
}
